import { useEffect, useState } from 'react'
import {
  Alert,
  Avatar,
  Badge,
  Box,
  Card,
  Heading,
  HoverCard,
  HStack,
  IconButton,
  SimpleGrid,
  Skeleton,
  Stack,
  Tabs,
  Text,
} from '@chakra-ui/react'
import { LuClock3, LuInfo } from 'react-icons/lu'
import { CapacityProgressChart, SibCapacityBudgetChart } from './CapacityCharts'
import type { DetailTab } from '../capacity.types'
import {
  CAPACITY_UNIT_BY_LABEL,
  capacityRestPct,
  formatQuantity,
  formatZeitzielIndex,
  parseNumber,
  PILOT_BANK,
  serviceLabel,
} from '../capacity.utils'
import { CapacityAreaMap } from './CapacityAreaMap'
import { UnionChangeTable } from './UnionChangeTable'
import { getCapacitySib, adaptSibItem, adaptAssignment } from '../../../../api/capacity.api'
import type { CapacitySibDetailDto } from '../../../../api/dto/capacity.dto'
import type { BankCapacityRow } from '../capacity.types'

function sibAlertReasons(frame: string[], ordered: string[], targetPct: number) {
  const services = frame.flatMap((f, index) => {
    const label = serviceLabel(index)
    const ord = ordered[index] ?? '0'
    const overflow = parseNumber(ord) - parseNumber(f)
    if (overflow > 0) {
      return [
        `${label} liegt mit ${formatQuantity(ord, CAPACITY_UNIT_BY_LABEL[label])} über der Kapazität von ${formatQuantity(f, CAPACITY_UNIT_BY_LABEL[label])}.`,
      ]
    }
    return []
  })
  const targetReason =
    targetPct < 85
      ? `Zeitziel liegt bei ${formatZeitzielIndex(String(targetPct))} und ist deutlich unter Plan.`
      : targetPct > 100
        ? `Zeitziel liegt bei ${formatZeitzielIndex(String(targetPct))} und ist vorgezogen.`
        : null
  const hasError = services.length > 0
  const hasWarning = Boolean(targetReason)
  return {
    services,
    targetReason,
    severity: hasError ? ('error' as const) : hasWarning ? ('warning' as const) : null,
  }
}

export function SibDetailPanel({
  userId,
  bankRows = [],
  year,
}: {
  userId: string
  bankRows?: BankCapacityRow[]
  /** The planning year to show; omitted for the running year (SIB-928). */
  year?: number
}) {
  const [dto, setDto] = useState<CapacitySibDetailDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [tab, setTab] = useState<DetailTab>('assignment')

  useEffect(() => {
    setLoading(true)
    setError(null)
    getCapacitySib(userId, year)
      .then(setDto)
      .catch(e => setError(e?.message || 'Fehler beim Laden der Daten'))
      .finally(() => setLoading(false))
  }, [userId, year])

  if (loading) {
    return (
      <Stack gap={4}>
        <Skeleton h="120px" borderRadius="md" />
        <Skeleton h="200px" borderRadius="md" />
        <Skeleton h="160px" borderRadius="md" />
      </Stack>
    )
  }

  if (error || !dto) {
    return (
      <Alert.Root status="error" variant="subtle">
        <Alert.Indicator />
        <Alert.Content>
          <Alert.Title>Fehler beim Laden</Alert.Title>
          <Alert.Description>{error ?? 'Unbekannter Fehler'}</Alert.Description>
        </Alert.Content>
      </Alert.Root>
    )
  }

  const sib = adaptSibItem(dto)
  const relatedAssignments = dto.assignments.map(adaptAssignment)

  const doneTotals = ['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'].map(code => {
    const svcEntry = dto.services.find(s => s.serviceCode === code)
    return String(svcEntry?.doneUnits ?? 0)
  })

  const anomalies = sibAlertReasons(sib.frame, sib.ordered, parseNumber(sib.target))

  return (
    <Box
      display="grid"
      gridTemplateColumns={{ base: '1fr', xl: 'minmax(0, 2fr) minmax(0, 1fr)' }}
      gap={6}
      alignItems="start"
    >
      <Stack gap={4}>
        {anomalies.severity && (
          <Alert.Root status={anomalies.severity} variant="subtle">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Auffälligkeit</Alert.Title>
              <Alert.Description>
                <Stack gap={1}>
                  {anomalies.services.map(r => (
                    <Text key={r} fontSize="sm">
                      {r}
                    </Text>
                  ))}
                  {anomalies.targetReason ? (
                    <Text fontSize="sm">{anomalies.targetReason}</Text>
                  ) : null}
                </Stack>
              </Alert.Description>
            </Alert.Content>
          </Alert.Root>
        )}

        <Tabs.Root
          value={tab}
          onValueChange={d => d.value && setTab(d.value as DetailTab)}
          size="sm"
          variant="subtle"
          colorPalette="gray"
        >
          <Tabs.List flexWrap="wrap">
            <Tabs.Indicator />
            {[
              { value: 'assignment', label: 'Zuordnung' },
              { value: 'trend', label: 'Verlauf' },
              { value: 'area', label: 'Wirkungsbereich' },
            ].map(item => (
              <Tabs.Trigger key={item.value} value={item.value}>
                {item.label}
              </Tabs.Trigger>
            ))}
          </Tabs.List>
        </Tabs.Root>

        {tab === 'trend' && (
          <Stack gap={4}>
            <SibCapacityBudgetChart
              sibName={sib.sib}
              frame={sib.frame}
              ordered={sib.ordered}
              done={doneTotals}
            />
            <CapacityProgressChart
              title={sib.sib}
              ordered={sib.ordered}
              done={doneTotals}
              monthlyData={dto.monthlyProgress}
            />
            <Stack gap={3}>
              <Heading size="md">Änderungen an Preisen und Kapazitäten</Heading>
              <UnionChangeTable fixedSibUserId={userId} initialSeenFilter="all" />
            </Stack>
          </Stack>
        )}

        {tab === 'area' && (
          <Card.Root variant="outline" borderColor="border" bg="bg" overflow="hidden">
            <CapacityAreaMap
              focus={{ name: sib.sib, place: sib.place, type: 'sib' }}
              others={dto.assignments.map(a => ({
                name: a.customerName,
                place:
                  bankRows.find(r => r.customerId === a.customerId)?.place ??
                  [a.customerPostal, a.customerCity].filter(Boolean).join(' '),
                type: 'bank' as const,
              }))}
            />
          </Card.Root>
        )}

        {tab === 'assignment' && (
          <Stack gap={3}>
            {relatedAssignments.length === 0 ? (
              <Card.Root variant="outline" borderColor="border" bg="bg">
                <Card.Body py={12} display="flex" alignItems="center" justifyContent="center">
                  <Text fontSize="sm" color="fg.subtle">
                    Keine Zuordnungen vorhanden.
                  </Text>
                </Card.Body>
              </Card.Root>
            ) : (
              relatedAssignments.map(assignment => (
                <Card.Root
                  key={`${assignment.sib}-${assignment.bank}`}
                  variant="outline"
                  borderColor="border"
                  bg="bg"
                >
                  <Card.Body p={6}>
                    <Stack gap={5}>
                      <Stack gap={0.5}>
                        <Text fontSize="md" fontWeight="semibold" color="fg">
                          {assignment.bank}
                        </Text>
                        <Text fontSize="sm" color="fg.subtle">
                          Laufzeit bis {assignment.until}
                        </Text>
                      </Stack>

                      <Stack gap={0}>
                        {(assignment.bank === PILOT_BANK ? [0, 1, 2, 3] : [0, 1, 2]).map(i => {
                          const label = serviceLabel(i)
                          const ordered = assignment.ordered[i] ?? '0'
                          const done = assignment.done[i] ?? '0'
                          const target = assignment.target[i] ?? '0'

                          return (
                            <SimpleGrid
                              key={label}
                              columns={{ base: 1, md: 3 }}
                              gap={4}
                              py={4}
                              borderTopWidth={i === 0 ? 0 : '1px'}
                              borderColor="border"
                              alignItems="center"
                            >
                              <Stack gap={0.5}>
                                <Text fontSize="xs" color="fg.subtle">
                                  Leistung
                                </Text>
                                <Text fontSize="sm" fontWeight="semibold" color="fg">
                                  {label}
                                </Text>
                              </Stack>

                              <Stack gap={0.5}>
                                <Text fontSize="xs" color="fg.subtle">
                                  Bestellt
                                </Text>
                                <Text fontSize="sm" fontWeight="medium" color="fg">
                                  {formatQuantity(ordered, CAPACITY_UNIT_BY_LABEL[label])}
                                </Text>
                              </Stack>

                              <Stack gap={0.5}>
                                <Text fontSize="xs" color="fg.subtle">
                                  Durchgeführt
                                </Text>

                                <HStack gap={2} align="center">
                                  <Text fontSize="sm" fontWeight="medium" color="fg">
                                    {formatQuantity(done, CAPACITY_UNIT_BY_LABEL[label])}
                                  </Text>

                                  <Badge
                                    colorPalette={parseNumber(target) < 85 ? 'red' : 'gray'}
                                    variant="subtle"
                                    size="sm"
                                    px={2}
                                  >
                                    <HStack gap={1}>
                                      <LuClock3 size={12} />
                                      <Text fontSize="xs" fontWeight="medium">
                                        Zeitziel {Math.round(parseNumber(target))} %
                                      </Text>
                                    </HStack>
                                  </Badge>
                                </HStack>
                              </Stack>
                            </SimpleGrid>
                          )
                        })}
                      </Stack>
                    </Stack>
                  </Card.Body>
                </Card.Root>
              ))
            )}
          </Stack>
        )}
      </Stack>

      <SibAside dto={dto} />
    </Box>
  )
}

function SibAside({ dto }: { dto: CapacitySibDetailDto }) {
  const sib = adaptSibItem(dto)
  const topics = sib.topics?.slice(0, 3) ?? []
  const extraTopics = sib.topics && sib.topics.length > 3 ? sib.topics.length - 3 : 0

  const summary = [0, 1, 2, 3].map(i => {
    const code = (['TOJ', 'CONSULTING', 'TRAINING', 'PILOT'] as const)[i]!
    const svcEntry = dto.services.find(s => s.serviceCode === code)
    return {
      label: serviceLabel(i),
      capacity: svcEntry?.capacityUnits ?? 0,
      ordered: svcEntry?.orderedUnits ?? 0,
      done: svcEntry?.doneUnits ?? 0,
    }
  })

  return (
    <Stack gap={4}>
      <Card.Root variant="outline" borderColor="border" bg="bg">
        <Card.Body>
          <Stack gap={4}>
            <HStack gap={3} align="center">
              <Avatar.Root size="xl" variant="subtle" colorPalette="blue">
                <Avatar.Fallback name={sib.sib} />
              </Avatar.Root>
              <Box minW={0} flex={1}>
                <Text
                  fontSize="xl"
                  fontWeight="semibold"
                  color="fg"
                  lineHeight="short"
                  overflow="hidden"
                  textOverflow="ellipsis"
                  whiteSpace="nowrap"
                >
                  {sib.sib}
                </Text>
              </Box>
              {sib.contacts?.length ? (
                <HoverCard.Root openDelay={120} closeDelay={80}>
                  <HoverCard.Trigger asChild>
                    <IconButton aria-label="Kontakt" size="xs" variant="ghost">
                      <LuInfo />
                    </IconButton>
                  </HoverCard.Trigger>
                  <HoverCard.Positioner zIndex={9999}>
                    <HoverCard.Content
                      bg="bg"
                      borderWidth="1px"
                      borderColor="border"
                      p={3}
                      shadow="md"
                      maxW="260px"
                    >
                      <HoverCard.Arrow />
                      <Stack gap={1}>
                        <Text fontSize="sm" fontWeight="semibold">
                          Kontakt
                        </Text>
                        {sib.contacts.map(c => (
                          <Text key={c} fontSize="sm" color="fg">
                            {c}
                          </Text>
                        ))}
                      </Stack>
                    </HoverCard.Content>
                  </HoverCard.Positioner>
                </HoverCard.Root>
              ) : null}
            </HStack>

            <SimpleGrid columns={2} gapX={8} gapY={4}>
              {(
                [
                  ['PLZ / Ort', sib.place],
                  ['Wirkungsradius', `${sib.radius} km`],
                  ['VG', sib.region],
                  ['Planumsatz', sib.revenue],
                ] as [string, string][]
              ).map(([label, value]) => (
                <Stack key={label} gap={1} minW={0}>
                  <Text fontSize="xs" color="fg.subtle">
                    {label}
                  </Text>
                  <Text fontSize="sm" fontWeight="medium" color="fg" lineHeight="short">
                    {value}
                  </Text>
                </Stack>
              ))}
            </SimpleGrid>
            <Stack gap={2}>
              <HStack gap={1.5} align="center">
                <Text fontSize="xs" color="fg.subtle">
                  Themen
                </Text>
                {sib.topics && sib.topics.length > 3 && (
                  <HoverCard.Root openDelay={120} closeDelay={80}>
                    <HoverCard.Trigger asChild>
                      <IconButton aria-label="Alle Themen" size="xs" variant="ghost">
                        <LuInfo />
                      </IconButton>
                    </HoverCard.Trigger>
                    <HoverCard.Positioner zIndex={9999}>
                      <HoverCard.Content
                        bg="bg"
                        borderWidth="1px"
                        borderColor="border"
                        p={3}
                        shadow="md"
                        maxW="260px"
                      >
                        <HoverCard.Arrow />
                        <Stack gap={1}>
                          <Text fontSize="sm" fontWeight="semibold">
                            Alle Themen
                          </Text>
                          {sib.topics.map(t => (
                            <Text key={t} fontSize="sm" color="fg">
                              {t}
                            </Text>
                          ))}
                        </Stack>
                      </HoverCard.Content>
                    </HoverCard.Positioner>
                  </HoverCard.Root>
                )}
              </HStack>
              {topics.length > 0 ? (
                <HStack gap={2} wrap="wrap">
                  {topics.map(t => (
                    <Badge key={t} colorPalette="gray" variant="subtle" size="sm">
                      {t}
                    </Badge>
                  ))}
                  {extraTopics > 0 && (
                    <Badge colorPalette="gray" variant="subtle" size="sm">
                      +{extraTopics} weitere
                    </Badge>
                  )}
                </HStack>
              ) : (
                <Text fontSize="sm" color="fg.subtle">
                  Keine Angabe
                </Text>
              )}
            </Stack>
          </Stack>
        </Card.Body>
      </Card.Root>

      <Card.Root variant="outline" borderColor="border" bg="bg">
        <Card.Body>
          <Stack gap={3}>
            <Text fontSize="sm" fontWeight="semibold" color="fg">
              Leistungsübersicht
            </Text>
            <Stack gap={0}>
              {summary.map((row, index) => {
                // const remaining = Math.max(row.capacity - row.ordered, 0)

                return (
                  <Stack
                    key={row.label}
                    gap={3}
                    py={3}
                    borderTopWidth={index === 0 ? 0 : '1px'}
                    borderColor="border"
                  >
                    <HStack align="center" gap={2}>
                      <Text fontSize="sm" fontWeight="semibold" color="fg">
                        {row.label}
                      </Text>
                      <Badge
                        colorPalette={row.ordered > row.capacity ? 'red' : 'gray'}
                        variant="subtle"
                        size="sm"
                      >
                        Restkapazität {capacityRestPct(String(row.ordered), String(row.capacity))}
                      </Badge>
                    </HStack>

                    <SimpleGrid columns={{ base: 1, xl: 3 }} gap={3}>
                      <Stack gap={1}>
                        <Text fontSize="xs" color="fg.subtle">
                          Kapazität
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatQuantity(String(row.capacity), CAPACITY_UNIT_BY_LABEL[row.label])}
                        </Text>
                      </Stack>

                      <Stack gap={1}>
                        <Text fontSize="xs" color="fg.subtle">
                          Bestellt
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatQuantity(String(row.ordered), CAPACITY_UNIT_BY_LABEL[row.label])}
                        </Text>
                      </Stack>

                      <Stack gap={1}>
                        <Text fontSize="xs" color="fg.subtle">
                          Durchgeführt
                        </Text>
                        <Text fontSize="sm" fontWeight="medium">
                          {formatQuantity(String(row.done), CAPACITY_UNIT_BY_LABEL[row.label])}
                        </Text>
                      </Stack>
                    </SimpleGrid>
                  </Stack>
                )
              })}
            </Stack>
          </Stack>
        </Card.Body>
      </Card.Root>
    </Stack>
  )
}
